import React, { useMemo } from 'react';

import PropTypes from 'prop-types';

import { useIdleTime } from '@adsk/bim360-matrix-react-api/es/contexts/idleTime';
import { useAccount } from '@adsk/bim360-matrix-react-api/es/contexts/account';

const parseIdleTimeout = account => {
  if (!account) return null;
  // Support both camelCase and snake_case in case account not parsed properly
  const idleTimeout = account.idleTimeout || account.idle_timeout;
  return !idleTimeout || typeof idleTimeout.enabled !== 'boolean' ? null : idleTimeout;
};

/**
 * Empty component to setup page idle logic while rendering the MartixHeader component.
 * It will try to setup the idle timer and signout the user once the idle time exceeded.
 */
const PageIdleTimer = ({
  pageIdleEnabled,
  pageIdleTimeInMs,
  signoutURL,
  selectedAccount,
  selectedProject,
  accounts,
  onSignout,
  onIdleLogout
}) => {
  const idleHandler = useIdleTime();
  const account = useAccount();
  const accountIdleTimeout = useMemo(() => {
    // Get the idleTimeout object from the account object
    let idleTimeout = parseIdleTimeout(account) || parseIdleTimeout(selectedAccount);

    // Filter out account from MatrixHeader accounts list
    if (!idleTimeout && selectedProject && selectedProject.accountId && Array.isArray(accounts)) {
      idleTimeout = parseIdleTimeout(
        accounts.find(account => account.id === selectedProject.accountId)
      );
    }

    // default to false, and use account idle only if enabled to avoid using the `duration`
    return !idleTimeout || idleTimeout.enabled !== true ? { enabled: false } : idleTimeout;
  }, [account, selectedAccount, selectedProject, accounts]);

  // Enabled in case any of the flags is true
  const enabled =
    (pageIdleEnabled !== undefined ? pageIdleEnabled : accountIdleTimeout.enabled) === true;
  const duration = pageIdleTimeInMs !== undefined ? pageIdleTimeInMs : accountIdleTimeout.duration;

  const handleSignOut = React.useCallback(() => {
    // Prefer to use onIdleLogout if provided by MatrixHeader component
    // Consumer can handle the sign out logic by themselves, instead of using the default logout.
    if (onIdleLogout) {
      onIdleLogout();
    } else {
      if (onSignout) onSignout();
      if (signoutURL) {
        window.location.href = signoutURL;
      }
    }
  }, [onIdleLogout, onSignout, signoutURL]);

  React.useEffect(() => {
    if (idleHandler) {
      idleHandler.update({ idleTimeInMs: duration, onSignOut: handleSignOut });
    }
  }, [idleHandler, duration, handleSignOut]);

  React.useEffect(() => {
    if (idleHandler) {
      idleHandler.toggle(enabled);
    }
  }, [idleHandler, enabled]);

  return <></>;
};

PageIdleTimer.propTypes = {
  /**
   * Whether enable the page idle timer and auto trigger logout once exceeded the idle time
   */
  pageIdleEnabled: PropTypes.bool,
  /**
   * The maximum page idle time in milliseconds
   */
  pageIdleTimeInMs: PropTypes.number,
  /**
   * The signout URL to redirect page if existed. Use from UserMenu component.
   */
  signoutURL: PropTypes.string,
  /**
   * The callback function when user sign out. Use from UserMenu component.
   */
  onSignout: PropTypes.func,
  /**
   * A function that's called when idle time exceeded. Higher priority than onSignout or signoutURL.
   */
  onIdleLogout: PropTypes.func,
  /**
   * The selected account object provided to MatrixHeader
   */
  selectedAccount: PropTypes.object,
  /**
   * The selected project object provided to MatrixHeader
   */
  selectedProject: PropTypes.object,
  /**
   * The accounts list provided to MatrixHeader
   */
  accounts: PropTypes.array
};

export default PageIdleTimer;
